<template>
  <BaseModal
    :visible="true"
    :title="modalTitle"
    size="xl"
    footer-align="start"
    elevated-footer
    @close="handleCancel"
  >
    <base-loader v-if="dataIsLoading" />

    <form
      v-else
      :id="formId"
      class="counterparty-form"
      @submit.prevent="saveCounterparty"
    >
      <div ref="formBodyRef">
        <counterparty-create-form
          class="counterparty-block"
          :formData="formData"
          @update:formData="updateFormData"
          ref="formRef"
        />

        <requisite-create
          v-if="createMode"
          ref="requisiteCreateRef"
          :allowed-models="['Counterparty']"
          :requisitable-type="'Counterparty'"
          :form-id="formId"
        />
      </div>
    </form>

    <template #footer>
      <base-primary-button
        type="button"
        :form="formId"
        :disabled="isSubmitting || dataIsLoading"
        @click="saveCounterparty"
      >
        <span v-if="isSubmitting">Сохранение...</span>
        <span v-else>Сохранить</span>
      </base-primary-button>

      <base-button
        type="button"
        @click="handleCancel"
        :disabled="isSubmitting"
      >
        Отмена
      </base-button>
    </template>
  </BaseModal>
</template>

<script>
import { getCompanyId } from "@/utils/userUtils";
import { useNotificationStore } from "@/stores/notificationStore";

import counterpartiesApi from "@/services/api/counterpartiesApi";

import CounterpartyCreateForm from "@/components/Counterparty/CounterpartyCreateForm.vue";
import RequisiteCreate from "@/components/Requisite/RequisiteCreate.vue";

export default {
  name: "AddCounterpartyModal",
  components: {
    CounterpartyCreateForm,
    RequisiteCreate,
  },
  props: {
    counterpartyId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ["close", "counterparty-created", "counterparty-updated"],
  data() {
    return {
      dataIsLoading: Boolean(this.counterpartyId),
      isSubmitting: false,
      formId: "counterparty-modal-form",

      companyId: getCompanyId(),

      formData: {
        name: "",
        inn: "",
        kpp: "",
        phone: "",
        email: "",
        city: "",
        street: "",
        house: "",
        legal_address: "",
        postal_address: "",
        postal_code: "",
        region_code: "",
        bank_name: "",
        bik: "",
        account_number: "",
        correspondent_account: "",
      },
    };
  },

  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    createMode() {
      return !this.counterpartyId;
    },

    modalTitle() {
      return this.createMode
        ? "Создание нового контрагента"
        : "Редактирование контрагента";
    },
  },

  methods: {
    updateFormData(newData) {
      Object.assign(this.formData, newData);
    },

    createFormData(counterparty = {}) {
      this.formData = {
        name: counterparty.name || "",
        inn: counterparty.inn || "",
        kpp: counterparty.kpp || "",
        phone: counterparty.phone || "",
        email: counterparty.email || "",
        city: counterparty.city || "",
        street: counterparty.street || "",
        house: counterparty.house || "",
        legal_address: counterparty.legal_address || "",
        postal_address: counterparty.postal_address || "",
        postal_code: counterparty.postal_code || "",
        region_code: counterparty.region_code || "",
        bank_name: counterparty.bank_name || "",
        bik: counterparty.bik || "",
        account_number: counterparty.account_number || "",
        correspondent_account:
          counterparty.correspondent_account || "",
      };
    },

    counterpartyPayload() {
      return {
        name: this.formData.name,
        inn: this.formData.inn,
        kpp: this.formData.kpp,
        phone: this.formData.phone,
        email: this.formData.email,
        city: this.formData.city,
        street: this.formData.street,
        house: this.formData.house,
        legal_address: this.formData.legal_address,
        postal_address: this.formData.postal_address,
        postal_code: this.formData.postal_code,
        region_code: this.formData.region_code,
        bank_name: this.formData.bank_name,
        bik: this.formData.bik,
        account_number: this.formData.account_number,
        correspondent_account: this.formData.correspondent_account,
      };
    },

    async saveCounterparty() {
      const formComponent = this.$refs.formRef;

      let isFormValid = true;
      if (
        formComponent &&
        typeof formComponent.validate === "function"
      ) {
        isFormValid = await formComponent.validate();
      }

      if (!isFormValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );

        this.$nextTick(() => {
          const firstErrorElement =
            this.$refs.formBodyRef?.querySelector(
              ".invalid, .error-message"
            );

          if (firstErrorElement) {
            const formGroup =
              firstErrorElement.closest(".form-group");
            const targetElement = formGroup || firstErrorElement;
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });

        return;
      }

      try {
        this.isSubmitting = true;

        if (this.createMode) {
          await this.createCounterparty();
        } else {
          await this.updateCounterparty();
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    async createCounterparty() {
      const response = await counterpartiesApi.create(
        this.companyId,
        this.counterpartyPayload()
      );

      const counterpartyId = response.data.result.id;

      await this.$refs.requisiteCreateRef.createRequisites(
        counterpartyId
      );

      this.notificationStore.showSuccess(
        "Новый контрагент успешно создан"
      );

      this.$emit("counterparty-created", response.data.result);
      this.$emit("close");
    },

    async updateCounterparty() {
      const response = await counterpartiesApi.update(
        this.companyId,
        this.counterpartyId,
        this.counterpartyPayload()
      );

      this.notificationStore.showSuccess(
        "Контрагент успешно обновлён"
      );

      this.$emit("counterparty-updated", response.data.result);
      this.$emit("close");
    },

    async fetchCounterparty() {
      if (this.createMode) return;

      this.dataIsLoading = true;

      try {
        const response = await counterpartiesApi.show(
          this.companyId,
          this.counterpartyId
        );

        this.createFormData(response.data.result);
      } finally {
        this.dataIsLoading = false;
      }
    },

    handleCancel() {
      this.$emit("close");
    },
  },

  mounted() {
    this.fetchCounterparty();
  },
};
</script>

<style scoped>
.counterparty-form {
  display: flex;
  flex-direction: column;
}

.counterparty-block {
  margin-bottom: var(--base-out-indent);
}
</style>
